/**
 * Offline cache coherence for multi-instance Small UPS (#996 Phase 1).
 * Keeps request.smallUpsConfigurations aligned with the small UPS API / React Query.
 * Mirrors rackOfflineSync.
 */

import { offlineCache, type OfflineSmallUpsOperation } from './offlineCache';
import { applyPendingRepeatableOperations } from './repeatableSectionOfflineSync';
import { groupSmallUpsByStatus } from '../components/smallups/smallUpsListUtils';

import type {
  GroupedSmallUpsConfigurations,
  SmallUpsConfiguration,
} from '../types/smallUps';

/** Small UPS list from cached request detail (offline reads). */
export async function getCachedSmallUpsList(
  requestId: string,
): Promise<SmallUpsConfiguration[]> {
  const cachedRequest = await offlineCache.getCachedRequestById(requestId);
  return cachedRequest?.smallUpsConfigurations ?? [];
}

/** Grouped units derived from cached list (same shape as GET …/small-ups/grouped). */
export async function getCachedSmallUpsGrouped(
  requestId: string,
): Promise<GroupedSmallUpsConfigurations> {
  return groupSmallUpsByStatus(await getCachedSmallUpsList(requestId));
}

/** Single unit from cached list when offline. */
export async function getCachedSmallUpsById(
  requestId: string,
  smallUpsId: number,
): Promise<SmallUpsConfiguration | undefined> {
  return (await getCachedSmallUpsList(requestId)).find(
    unit => unit.id === smallUpsId,
  );
}

/**
 * After a successful online small UPS fetch or mutation refetch, mirror units onto
 * the cached request so offline reads stay consistent.
 */
export async function mirrorSmallUpsToOfflineRequestCache(
  requestId: string,
  smallUps: SmallUpsConfiguration[],
): Promise<void> {
  const cachedRequest = await offlineCache.getCachedRequestById(requestId);
  if (!cachedRequest) {
    return;
  }
  await offlineCache.updateCachedRequestSmallUps(requestId, smallUps);
}

/** Pending offline small UPS queue entries for one request (create/update/delete/reorder). */
export async function getPendingSmallUpsOpsForRequest(
  requestId: string,
): Promise<
  Awaited<ReturnType<typeof offlineCache.getPendingSmallUpsOperations>>
> {
  const pending = await offlineCache.getPendingSmallUpsOperations();
  return pending.filter(op => String(op.requestId) === String(requestId));
}

/**
 * In-flight offline small UPS ops (status `pending` OR `syncing`) for one request.
 * Used by the ONLINE overlay: the sync flips an op to `syncing` BEFORE its POST,
 * so a `pending`-only view drops an op mid-sync and the item vanishes during the
 * reconnect race. Including `syncing` keeps it visible until the op is dequeued.
 */
export async function getInFlightSmallUpsOpsForRequest(
  requestId: string,
): Promise<OfflineSmallUpsOperation[]> {
  const queue = await offlineCache.getOfflineSmallUpsQueue();
  return queue.filter(
    op =>
      (op.status === 'pending' || op.status === 'syncing') &&
      String(op.requestId) === String(requestId),
  );
}

/**
 * Overlay pending offline small UPS operations onto a base list so offline reads
 * reflect not-yet-synced changes. Pure and deterministic (ops applied in
 * timestamp order). De-dupes creates by id so an optimistic unit already written
 * into the base list is not duplicated. (mirrors #995 bug 6)
 */
export function applyPendingSmallUpsOperations(
  requestId: string,
  base: SmallUpsConfiguration[],
  ops: OfflineSmallUpsOperation[],
): SmallUpsConfiguration[] {
  return applyPendingRepeatableOperations(requestId, base, ops, {
    instanceId: op => op.smallUpsId,
    reorderIds: op => op.smallUpsIds,
  });
}

/** Cached small UPS list with pending offline operations overlaid (offline reads). */
export async function getOfflineSmallUpsList(
  requestId: string,
): Promise<SmallUpsConfiguration[]> {
  const [base, ops] = await Promise.all([
    getCachedSmallUpsList(requestId),
    getPendingSmallUpsOpsForRequest(requestId),
  ]);
  return applyPendingSmallUpsOperations(requestId, base, ops);
}

/** Grouped units (existing/new) from the overlaid offline list. */
export async function getOfflineSmallUpsGrouped(
  requestId: string,
): Promise<GroupedSmallUpsConfigurations> {
  return groupSmallUpsByStatus(await getOfflineSmallUpsList(requestId));
}

/** Single unit from the overlaid offline list (includes pending creates). */
export async function getOfflineSmallUpsById(
  requestId: string,
  smallUpsId: number,
): Promise<SmallUpsConfiguration | undefined> {
  return (await getOfflineSmallUpsList(requestId)).find(
    unit => unit.id === smallUpsId,
  );
}
